import React, { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Card, ListGroup, Modal, Button, ButtonToolbar, Dropdown, Spinner } from 'react-bootstrap';
import { del, post, get } from '../api2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faTimes } from '@fortawesome/free-solid-svg-icons';
import RelativeTime from '../Components/RelativeTime';
import NewBadge from '../Components/NewBadge';
import SearchInput from '../Components/SearchInput';
import { hex } from '../helpers';

const pinnedBlocklists = { 'nextdns-recommended': 0, 'nextdns-youtube': 1 };

export default function PrivacyBlocklists({ blocklists: initialBlocklists, profile, role }) {
  const { t, i18n } = useTranslation();
  const [allBlocklistsLoaded, setAllBlocklistsLoaded] = useState(false);
  const [blocklists, setBlocklists] = useState(initialBlocklists.map((blocklist, popIndex) => ({
    ...blocklist,
    added: true,
    popIndex,
  })));
  const [addedOrder, setAddedOrder] = useState(initialBlocklists.map(blocklist => blocklist.id));
  const [blocklistsSortBy, setBlocklistsSortBy] = useState('popularity');
  const [blocklistsSearch, setBlocklistsSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const source = useRef();

  useEffect(() => {
    source.current = new Set();
    return () => {
      source.current.forEach(func => func());
    }
  }, []);

  const loadAllBlocklists = () => {
    if (!allBlocklistsLoaded) {
      const cancel = get(`/privacy/blocklists`, { locale: i18n.language }, ({ data }) => {
        setBlocklists(prevBlocklists => data.map((blocklist, popIndex) => ({
          ...blocklist,
          added: prevBlocklists.some(b => b.id === blocklist.id && b.added),
          popIndex,
        })));
        setAllBlocklistsLoaded(true);
      });
      source.current.add(cancel);
    }
  };

  const openModal = () => {
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
  };

  const add = (id) => {
    setBlocklists(current =>
      current.map(b => b.id === id ? { ...b, added: true } : b)
    );
    setAddedOrder(current => [...current, id]);
    const cancel = post(`/profiles/${profile}/privacy/blocklists`, { id });
    source.current.add(cancel);
  };

  const remove = (id) => {
    setBlocklists(current =>
      current.map(b => ({
        ...b,
        added: b.id === id ? false : b.added
      }))
    );
    setAddedOrder(current => current.filter(i => i !== id));
    const cancel = del(`/profiles/${profile}/privacy/blocklists/${hex(id)}`);
    source.current.add(cancel);
  };

  const blocklistsTransformed = () => {
    let filteredBlocklists = [...blocklists];

    if (blocklistsSearch) {
      const search = blocklistsSearch.toLocaleLowerCase(i18n.language);
      filteredBlocklists = filteredBlocklists.filter(blocklist =>
        t(`privacy.blocklists.blocklists.${blocklist.id}.name`, blocklist.name)
          .toLocaleLowerCase(i18n.language).includes(search)
      );
    }

    return filteredBlocklists.sort((a, b) => {
      const pinnedOrder = (pinnedBlocklists[a.id] ?? 2) - (pinnedBlocklists[b.id] ?? 2);
      if (pinnedOrder) return pinnedOrder;

      switch (blocklistsSortBy) {
        case 'name':
          return t(`privacy.blocklists.blocklists.${a.id}.name`, a.name).localeCompare(
            t(`privacy.blocklists.blocklists.${b.id}.name`, b.name),
            i18n.language
          );
        case 'entries':
          return b.entries - a.entries;
        case 'updated':
          return new Date(b.updatedOn) - new Date(a.updatedOn);
        default:
          return a.popIndex - b.popIndex;
      }
    });
  };

  const addedBlocklists = () => {
    return blocklists.filter(blocklist => blocklist.added).sort((a, b) => {
      return addedOrder.indexOf(a.id) - addedOrder.indexOf(b.id);
    });
  }

  return (
    <>
      <Card>
        <ListGroup variant="flush">
          <ListGroup.Item className="py-3">
            <h5>{t('privacy.blocklists.name')}</h5>
            <div style={{ opacity: 0.6, fontSize: '0.9em' }}>{t('privacy.blocklists.description')}</div>
          </ListGroup.Item>
          {addedBlocklists().map((blocklist, index) => (
            <ListGroup.Item key={blocklist.id} style={{ borderLeft: '4px solid #007bff' }}>
              <div className="d-flex">
                <div className="flex-grow-1" style={{ marginLeft: -4 }}>
                  <BlocklistDetails blocklist={blocklist} />
                </div>
                {role !== 'viewer' && (
                  <div className="ms-4">
                    <Button
                      variant="link"
                      aria-label={`${t('global.remove')} ${t(
                        `privacy.blocklists.blocklists.${blocklist.id}.name`,
                        blocklist.name
                      )}`}
                      className="me-n3 mt-n1"
                      style={{ opacity: 0.3, color: '#222' }}
                      onClick={() => remove(blocklist.id)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                )}
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
        <Card.Footer className="text-muted" style={{ borderTop: 'none' }}>
          <Button
            size="sm"
            style={{ fontWeight: 'bold', fontSize: 12, textTransform: 'uppercase' }}
            onClick={openModal}
            disabled={role === 'viewer'}
          >
            {t('privacy.blocklists.add')}
          </Button>
        </Card.Footer>
      </Card>

      <Modal
        show={showModal}
        animation={false}
        size="lg"
        scrollable={true}
        onHide={closeModal}
        onShow={loadAllBlocklists}
      >
        <Modal.Header closeButton>
          <Modal.Title as="h5">{t('privacy.blocklists.add')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {!allBlocklistsLoaded ? (
            <div className="text-center py-3">
              <Spinner animation="border" style={{ width: '2rem', height: '2rem', opacity: 0.15 }} className="my-4" />
            </div>
          ) : (
            <>
              <ButtonToolbar className="m-2 me-3 ms-3 d-flex gap-2 flex-nowrap justify-content-between">
                <SearchInput active className="me-2 flex-grow-1" value={blocklistsSearch} onChange={setBlocklistsSearch} />
                <Dropdown className="flex-shrink-0">
                  <Dropdown.Toggle variant="light" size="sm">
                    <FontAwesomeIcon icon={faSort} style={{ marginRight: "8px" }} />
                    {t(`privacy.blocklists.sortBy.${blocklistsSortBy}`)}
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    {['popularity', 'name', 'entries', 'updated'].map((sortBy) => (
                      <Dropdown.Item
                        onClick={() => setBlocklistsSortBy(sortBy)}
                        size="sm"
                        active={blocklistsSortBy === sortBy}
                      >
                        {t(`privacy.blocklists.sortBy.${sortBy}`)}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </ButtonToolbar>
              <ListGroup variant="flush">
                {blocklistsTransformed().map((blocklist) => (
                  <ListGroup.Item
                    key={blocklist.id}
                    style={{ borderLeft: '4px solid #ccc', borderLeftColor: blocklist.added ? '#007bff' : '' }}
                  >
                    <div className="d-lg-flex gap-4" style={{ marginLeft: -4 }}>
                      <div className="flex-grow-1">
                        <BlocklistDetails
                          blocklist={blocklist}
                          isNew={blocklist.id === 'shadowwhisperer-tracking'}
                        />
                      </div>
                      <div className="d-flex align-items-center my-2 my-lg-0">
                        {blocklist.added && (
                          <Button
                            variant="danger"
                            size="sm"
                            style={{
                              fontWeight: 'bold',
                              fontSize: 12,
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                            }}
                            onClick={() => remove(blocklist.id)}
                          >
                            {t('global.remove')}
                          </Button>
                        )}
                        {!blocklist.added && (
                          <Button
                            size="sm"
                            style={{
                              fontWeight: 'bold',
                              fontSize: 12,
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                            }}
                            onClick={() => add(blocklist.id)}
                          >
                            {t('global.add')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

function BlocklistDetails({ blocklist, isNew }) {
  const { t } = useTranslation();
  const youtube = blocklist.id === 'nextdns-youtube';

  return (
    <>
      <div className="d-flex align-items-center">
        <div className="notranslate" style={{ fontWeight: 500 }}>
          {t(`privacy.blocklists.blocklists.${blocklist.id}.name`, blocklist.name)}
        </div>
        {isNew && <NewBadge releasedOn="2026-11-05" />}
        {youtube && <span className="beta-badge mt-0">{t('global.beta')}</span>}
      </div>
      <div className="mt-1" style={{ fontSize: '0.9em', opacity: 0.5 }}>
        {blocklist.description}
      </div>
      <div className="mt-1" style={{ fontSize: '0.8em' }}>
        {blocklist.website !== null && (
          <div className="d-sm-inline">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={blocklist.website}
              className="notranslate"
              style={{ wordBreak: 'break-all' }}
            >
              {blocklist.website.replace(/^https?:\/\//, '')}
            </a>
            <span className="d-none d-sm-inline" style={{ opacity: 0.4 }}>
              {' '}
              •{' '}
            </span>
          </div>
        )}
        <div className="d-sm-inline mt-1 mt-sm-0">
          <span style={{ opacity: 0.4 }}>
            {t('privacy.blocklists.entries', { count: youtube ? 2 : blocklist.entries })}
          </span>
          <span style={{ opacity: 0.4 }}> • </span>
          <span style={{ opacity: 0.4 }}>
            <Trans i18nKey="privacy.blocklists.updated">
              {'_Updated '}
              <BlocklistUpdated blocklist={blocklist} />
            </Trans>
          </span>
        </div>
      </div>
    </>
  );
}

function BlocklistUpdated({ blocklist }) {
  const { i18n } = useTranslation();
  return blocklist.id === 'nextdns-youtube'
    ? new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' }).format(0, 'second')
    : <RelativeTime date={blocklist.updatedOn} />;
}
